/**
 * 🏗️ DOMAINS DATA CLIENT
 *
 * Type-safe domain operations with validation and error handling.
 * Follows Convex best practices for shared client code.
 */
import { api } from "@convex/_generated/api";
import { validateUpdateDomainRequest, validateCreateDomainRequest } from "./validation";
import { BaseDataClient } from "./baseClient";
/**
 * Domain-specific data client with type-safe operations
 */
export class DomainsClient extends BaseDataClient {
    /**
     * Get a domain by name
     *
     * @param domain Domain name (e.g., "example.com")
     * @returns Domain data or null if not found
     */
    async getDomainByName(domain) {
        return await this.client.query(api.domains.getDomainByName, { domain });
    }
    /**
     * Get a domain by ID
     *
     * @param id Domain ID
     * @returns Domain data or null if not found
     */
    async getDomainById(id) {
        return await this.client.query(api.domains.getDomainById, { id: id });
    }
    /**
     * Create a new domain
     *
     * @param dto Domain creation data
     * @returns Created domain result
     */
    async createDomain(dto) {
        const validatedArgs = validateCreateDomainRequest(dto);
        return await this.client.mutation(api.domains.createDomain, validatedArgs);
    }
    /**
     * Update an existing domain
     *
     * @param dto Domain update data
     * @returns Update result
     */
    async updateDomain(dto) {
        const validatedArgs = validateUpdateDomainRequest(dto);
        return await this.client.mutation(api.domains.updateDomain, validatedArgs);
    }
    /**
     * Delete a domain
     *
     * @param id Domain ID to delete
     * @returns Delete result
     */
    async deleteDomain(id) {
        return await this.client.mutation(api.domains.deleteDomain, { id: id });
    }
    /**
     * Get all domains for a user
     *
     * @param userId User ID
     * @returns Array of user's domains
     */
    async getUserDomains(userId) {
        return await this.client.query(api.domains.getUserDomains, { userId });
    }
    /**
     * Search user domains with filters and facets
     *
     * @param userId User ID
     * @param filters Search filters
     * @returns Search results with facets
     */
    async searchUserDomains(userId, filters = {}) {
        return await this.client.query(api.domains.searchUserDomainsClean, {
            userId,
            page: 1, // Provide default required values
            pageSize: 20,
            ...filters
        });
    }
    /**
     * Add tags to multiple domains (bulk operation)
     */
    async bulkAddTags(domainIds, tags) {
        return await this.client.mutation(api.domains.bulkAddTags, {
            domainIds: domainIds,
            tags
        });
    }
    /**
     * Remove tags from multiple domains (bulk operation)
     */
    async bulkRemoveTags(domainIds, tags) {
        return await this.client.mutation(api.domains.bulkRemoveTags, {
            domainIds: domainIds,
            tags
        });
    }
    /**
     * Initiate domain verification
     */
    async initiateDomainVerification(domainId, method) {
        return await this.client.mutation(api.domains.initiateDomainVerification, {
            domainId: domainId,
            method
        });
    }
    /**
     * Verify domain ownership
     */
    async verifyDomain(domainId) {
        return await this.client.action(api.domains.verifyDomain, {
            domainId: domainId
        });
    }
    /**
     * Manually sync nameservers from registrar
     */
    async syncNameservers(userId, domain, registrar) {
        try {
            // Call the appropriate refresh action based on registrar
            if (registrar === 'spaceship') {
                await this.client.action(api.spaceship.refreshSpaceshipNameservers, { userId, domain });
            }
            else if (registrar === 'namecom') {
                await this.client.action(api.namecom.refreshNamecomNameservers, { userId, domain });
            }
            else if (registrar === 'namecheap') {
                await this.client.action(api.namecheap.refreshNamecheapNameservers, { userId, domain });
            }
            else {
                return {
                    success: false,
                    error: `Nameserver sync not supported for registrar: ${registrar || 'unknown'}`
                };
            }
            // Get updated domain to return nameservers
            const updatedDomain = await this.getDomainByName(domain);
            return {
                success: true,
                nameservers: updatedDomain?.nameservers || []
            };
        }
        catch (error) {
            return {
                success: false,
                error: error?.message || 'Failed to sync nameservers'
            };
        }
    }
    /**
     * Get all domain IDs for user with filters
     */
    async getAllDomainIds(params) {
        return await this.client.action(api.domains.getAllDomainIds, params);
    }
}
/**
 * Factory function to create a DomainsClient
 *
 * @param client Configured ConvexHttpClient
 * @returns DomainsClient instance
 */
export function createDomainsClient(client) {
    return new DomainsClient(client);
}
